import io, { Socket } from 'socket.io-client';

import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import config from '../envConfig';
import {
  NOTIFICATIONS_TAG,
  UNREAD_COUNT_TAG,
  UNREAD_COUNT_TAG_ID,
  notificationsApi,
} from '../redux/api/notificationsApi';
import { trainingsApi } from '../redux/api/trainingsAPI';
import { usersApi } from '../redux/api/users/usersApi';
import { useLazyGetMyDetails } from '../redux/api/users/usersApi';
import { REDUX_CACHE, TRAININGS, USERS } from '../redux/constants';
import { authTokenHolder } from '../shared/auth/auth-token-holder';
import {
  ITrainingChangeEventPayload,
  TrainingChangeTypes,
  WebSocketEvents,
} from '../shared/websocket-events';
import { scheduleDeferredStartupTask } from '../utils/startup/schedule-deferred-startup-task';
import { LoginUserStatus, useAuth } from './AuthContext';

interface IWebSocketListener {
  id: string;
  type: string;
  handler: (...args: any[]) => void;
}

interface IWebSocketContextValue {
  addListener: (
    id: string,
    type: string,
    handler: (...args: any[]) => void,
  ) => void;
  removeListener: (id: string) => void;
}
const WebSocketContext = React.createContext<
  IWebSocketContextValue | undefined
>(undefined);

const wsUrl = config.wsUrl;

function getSocketConnectionConfig(url: string) {
  const parsedUrl = new URL(url);
  const normalizedPath = parsedUrl.pathname.replace(/\/+$/, '');
  const socketPath =
    normalizedPath && normalizedPath !== '/' ? normalizedPath : '/ws';

  return {
    baseUrl: parsedUrl.origin,
    path: socketPath,
  };
}

/** Min delay between reconnect attempts (Socket.IO exponential backoff starts here). */
const WS_RECONNECTION_DELAY_MS = 10_000;
/** Cap backoff so a dead server does not hammer the client or DevTools. */
const WS_RECONNECTION_DELAY_MAX_MS = 120_000;

const NOTIFICATION_INVALIDATION_TAGS = [
  NOTIFICATIONS_TAG,
  { type: UNREAD_COUNT_TAG, id: UNREAD_COUNT_TAG_ID },
] as const;

const SUBSCRIPTION_INVALIDATION_TAGS = [
  USERS.ME,
  USERS.ME_DETAILED,
  USERS.RESTRICTION_PERMISSIONS,
  USERS.USER_SUBSCRIPTIONS,
] as const;

function getTrainingInvalidationTags(payload: ITrainingChangeEventPayload) {
  const detailTag = payload?.trainingId
    ? [{ type: TRAININGS.TRAININGS, id: payload.trainingId }]
    : [];

  switch (payload?.type) {
    case TrainingChangeTypes.createdForOwner:
    case TrainingChangeTypes.changedForOwner:
      return [
        ...detailTag,
        { type: TRAININGS.TRAININGS, id: REDUX_CACHE.LIST },
        { type: TRAININGS.CREATED_FOR_OTHERS, id: REDUX_CACHE.LIST },
      ];

    case TrainingChangeTypes.changedForCreator:
      return [
        ...detailTag,
        { type: TRAININGS.CREATED_FOR_OTHERS, id: REDUX_CACHE.LIST },
      ];

    case TrainingChangeTypes.sharedWithUser:
    case TrainingChangeTypes.exerciseDeletedForOwner:
    case TrainingChangeTypes.exerciseDeletedForCreator:
      return [
        ...detailTag,
        { type: TRAININGS.TRAININGS, id: REDUX_CACHE.LIST },
        { type: TRAININGS.AVAILABLE, id: REDUX_CACHE.LIST },
      ];

    default:
      return detailTag;
  }
}

interface IWebSocketProviderProps {
  children: React.ReactNode;
}

export function WebSocketProvider(props: IWebSocketProviderProps) {
  const dispatch = useDispatch();
  const [getMyDetails, { data: user }] = useLazyGetMyDetails();
  const { loginUserStatus } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const listenersRef = useRef<IWebSocketListener[]>([]);

  const refreshNotifications = () => {
    dispatch(
      notificationsApi.util.invalidateTags([...NOTIFICATION_INVALIDATION_TAGS]),
    );
  };

  const refreshSubscriptionRelatedData = () => {
    dispatch(usersApi.util.invalidateTags([...SUBSCRIPTION_INVALIDATION_TAGS]));
  };

  useEffect(() => {
    if (loginUserStatus === LoginUserStatus.loggedIn) {
      getMyDetails();
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, [loginUserStatus, getMyDetails]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const initializeSocket = async () => {
      if (socketRef.current) socketRef.current.disconnect();

      const token = await authTokenHolder.getToken();
      if (cancelled) return;
      if (!token) return;
      const { baseUrl, path } = getSocketConnectionConfig(wsUrl);

      const socket = io(baseUrl, {
        path,
        addTrailingSlash: false,
        transports: ['websocket'],
        withCredentials: true,
        query: { userId: user.id },
        auth: { token },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: WS_RECONNECTION_DELAY_MS,
        reconnectionDelayMax: WS_RECONNECTION_DELAY_MAX_MS,
        randomizationFactor: 0.5,
      });

      socket.on('reconnect_attempt', async () => {
        const nextToken = await authTokenHolder.getToken();
        if (!nextToken) {
          socket.disconnect();
          return;
        }
        socket.auth = { token: nextToken };
      });

      socket.on('connect_error', async (error) => {
        const nextToken = await authTokenHolder.getToken();
        if (!nextToken) {
          socket.disconnect();
          return;
        }
        socket.auth = { token: nextToken };
      });

      socket.on(
        WebSocketEvents.trainingChange,
        (payload: ITrainingChangeEventPayload) => {
          const tagsToInvalidate = getTrainingInvalidationTags(payload);
          if (!tagsToInvalidate.length) return;
          dispatch(trainingsApi.util.invalidateTags(tagsToInvalidate));
        },
      );

      socket.on(WebSocketEvents.notificationCreated, () => {
        refreshNotifications();
      });

      socket.on(WebSocketEvents.subscriptionActivated, () => {
        refreshSubscriptionRelatedData();
      });

      socket.on('connect', () => {
        refreshNotifications();
        refreshSubscriptionRelatedData();
      });

      listenersRef.current.forEach((listener) => {
        socket.on(listener.type, listener.handler);
      });

      socketRef.current = socket;
    };

    const cancelDeferredSocketInit = scheduleDeferredStartupTask(() => {
      void initializeSocket();
    });

    return () => {
      cancelled = true;
      cancelDeferredSocketInit();
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [user, dispatch]);

  function addListener(
    id: string,
    type: string,
    handler: (...args: any[]) => void,
  ) {
    removeListener(id);
    listenersRef.current = [...listenersRef.current, { id, type, handler }];

    if (socketRef.current) socketRef.current.on(type, handler);
  }

  function removeListener(id: string) {
    const listener = listenersRef.current?.find((l) => l.id === id);
    listenersRef.current = listenersRef.current?.filter((l) => l.id !== id);

    if (listener && socketRef.current)
      socketRef.current.off(listener.type, listener.handler);
  }

  return (
    <WebSocketContext.Provider
      value={{
        addListener,
        removeListener,
      }}>
      {props.children}
    </WebSocketContext.Provider>
  );
}

function useWebSocket() {
  const context = React.useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within an WebSocketProvider');
  }

  return context;
}

export { useWebSocket, WebSocketContext };
